import type { BillingInterval } from "./types.js";

/** Annual bump price = 10× monthly (two months free, aligned with plan annual discount). */
export const ORDER_BUMP_ANNUAL_MULTIPLIER = 10;

/**
 * zenpost-api `AppFeature.key` values in `group=bonus`. A bump key outside this
 * list would be sold without unlocking anything in the app, so the admin only
 * accepts these.
 */
export const BONUS_FEATURE_KEYS = [
  "logo_generator",
  "fixed_posts_generator",
  "instagram_bio",
  "bunker_course",
  "zenlinks",
  "zenpages",
] as const;

export type OrderBumpSource = "gift" | "selected";

/**
 * The add-on the checkout highlights among the paid ones: the best seller since
 * it was turned on (17% of new payers in its first days, against 14-15% for the
 * next ones).
 */
export const FEATURED_BUMP_KEY = "zenlinks";

/**
 * Reference ("de") price shown struck through next to a paid add-on: the one set
 * in the catalog (`compare_at_monthly_cents`), or else the price as if it were 30%
 * off, rounded to whole reais — R$ 5 shows "de R$ 7", R$ 7 shows "de R$ 10". The
 * page strikes it only when it is above the price, so a catalog reference at or
 * below the price shows none. Display only: nothing is ever charged at this price
 * and the combo never reads it.
 */
export const BUMP_REFERENCE_DISCOUNT = 0.3;

export function bumpReferenceMonthlyCents(priceMonthlyCents: number, compareAtMonthlyCents?: number | null): number {
  if (compareAtMonthlyCents != null) return compareAtMonthlyCents;
  return Math.round(priceMonthlyCents / (1 - BUMP_REFERENCE_DISCOUNT) / 100) * 100;
}

/**
 * "Take them all": every paid add-on on offer to the buyer for one monthly price
 * (annual = 10×, like each add-on). Only worth showing — and only applied — when
 * two or more are on offer and their list prices add up to more than this.
 * Changing it never reprices a sold combo: each line keeps the share it was sold
 * at (R$ 15 until Zenpages joined the catalog, R$ 19 since).
 */
export const BUMP_COMBO_MONTHLY_CENTS = 1900;

export function bumpComboApplies(monthlyPrices: number[]): boolean {
  const sum = monthlyPrices.reduce((total, cents) => total + cents, 0);
  return monthlyPrices.length >= 2 && sum > BUMP_COMBO_MONTHLY_CENTS;
}

/**
 * The combo price split across its add-ons in proportion to their list prices,
 * in whole cents that add up to exactly BUMP_COMBO_MONTHLY_CENTS (the leftover
 * cents go to the first lines). Stored as each line's monthly price, so
 * renewals, plan changes and reactivations keep charging the combo price
 * without knowing a combo exists.
 */
export function allocateBumpComboMonthlyCents(monthlyPrices: number[]): number[] {
  const sum = monthlyPrices.reduce((total, cents) => total + cents, 0);
  const shares = monthlyPrices.map((cents) => Math.floor((cents * BUMP_COMBO_MONTHLY_CENTS) / sum));
  let leftover = BUMP_COMBO_MONTHLY_CENTS - shares.reduce((total, cents) => total + cents, 0);
  for (let i = 0; leftover > 0; i = (i + 1) % shares.length, leftover--) shares[i] = (shares[i] ?? 0) + 1;
  return shares;
}

/**
 * The add-ons a link asks the checkout to open already ticked (`?bump=zenlinks`):
 * the zenpost /previews page sends it from its "Quero minha página" button, so a
 * lead who chose the page from its preview does not have to tick it again. Only
 * known keys, at most one of each.
 */
export function requestedBumpKeys(raw: string | null | undefined): string[] {
  const known = new Set<string>(BONUS_FEATURE_KEYS);
  const keys = (raw ?? "").split(",").map((key) => key.trim()).filter((key) => known.has(key));
  return [...new Set(keys)];
}

/**
 * The selection with the requested add-ons ticked, for those on sale in `items`.
 * A gift is already included and an add-on not for sale stays out, so neither is
 * added. Returns `prev` itself when nothing changes.
 */
export function preselectRequestedBumps(
  prev: Set<string>,
  items: { key: string; kind: "offer" | "gift" }[],
  requested: string[],
): Set<string> {
  const onSale = new Set(items.filter((item) => item.kind === "offer").map((item) => item.key));
  const add = requested.filter((key) => onSale.has(key) && !prev.has(key));
  return add.length ? new Set([...prev, ...add]) : prev;
}

/**
 * Checkout order: what the bot already promised first (it is what the buyer was
 * told about in the conversation), then the featured add-on, then the other
 * paid ones in catalog order. Only a featured add-on that is for sale is marked.
 */
export function arrangeCheckoutBumpItems<T extends { key: string; kind: "offer" | "gift" }>(
  items: T[],
): (T & { featured: boolean })[] {
  const gifts = items.filter((item) => item.kind === "gift").map((item) => ({ ...item, featured: false }));
  const offers = items.filter((item) => item.kind === "offer");
  const featured = offers.filter((item) => item.key === FEATURED_BUMP_KEY).map((item) => ({ ...item, featured: true }));
  const rest = offers.filter((item) => item.key !== FEATURED_BUMP_KEY).map((item) => ({ ...item, featured: false }));
  return [...gifts, ...featured, ...rest];
}

export function orderBumpListCents(priceMonthlyCents: number, interval: BillingInterval): number {
  if (interval === "annual") return priceMonthlyCents * ORDER_BUMP_ANNUAL_MULTIPLIER;
  return priceMonthlyCents;
}

export function sumPaidBumpCents(
  lines: { chargedCents: number }[],
): number {
  return lines.reduce((sum, line) => sum + line.chargedCents, 0);
}

/**
 * Recurring cents the paid add-ons add for `interval`, priced from the monthly
 * price stored on each line when it was sold — a later catalog edit or a
 * deactivated bump never changes what an existing subscriber pays.
 */
export function recurringBumpCents(
  lines: { source: string; monthlyCents: number }[],
  interval: BillingInterval,
): number {
  return lines
    .filter((line) => line.source === "selected")
    .reduce((sum, line) => sum + orderBumpListCents(line.monthlyCents, interval), 0);
}

export function collectBumpKeys(lines: { bumpKey: string }[]): string[] {
  return [...new Set(lines.map((l) => l.bumpKey))].sort();
}
